import { apiService } from './api';

export interface ReturnBooksRequest {
  loanIds: string[];
}

export interface ReturnBooksResponse {
  success: boolean;
  message: string;
  returnedCount: number;
}

export interface CheckoutBooksRequest {
  userId: string;
  bookIds: string[];
  dueDates?: { bookId: string; dueDate: string }[]; // Ngày trả cho từng sách (nếu có)
}

export interface CheckoutBooksResponse {
  success: boolean;
  message: string;
  successfulRequestIds?: string[]; // IDs of loan requests created
  successfulLoanIds?: string[]; // Legacy: IDs of loans created (for backward compatibility)
  failedBookIds?: Array<{
    bookId: string;
    reason: 'NotFound' | 'NotAvailable' | 'DatabaseError';
  }>;
}

export interface LoanRequest {
  id: string;
  bookId: string;
  userId: string;
  requestedDate: string;
  dueDate: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedByUserId?: string;
  reviewedDate?: string;
  rejectionReason?: string;
  notes?: string;
  book: {
    id: string;
    title: string;
    author: string;
    availableCopies: number;
    totalCopies: number;
  };
  user: {
    id: string;
    fullName: string;
    email?: string;
    phoneNumber?: string;
  };
  reviewer?: {
    fullName: string;
  } | null;
}

export interface GetLoanRequestsResponse {
  success: boolean;
  requests: LoanRequest[];
  total?: number; // Legacy field
  totalCount?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
}

export interface ApproveLoanRequestResponse {
  success: boolean;
  message: string;
  loanId: string;
  requestId: string;
}

export interface RejectLoanRequestResponse {
  success: boolean;
  message: string;
  requestId: string;
}

export interface BookLoan {
  id: string;
  loanId?: string; // For consistency with overdue books
  loanDate: string | null;
  dueDate: string | null;
  returnDate: string | null;
  returnStatus?: 'on_time' | 'overdue' | null; // 'on_time' = Đúng hạn, 'overdue' = Trễ hạn
  book: {
    id: string;
    title: string;
    author: string;
    isbn?: string;
  };
  user: {
    id: string;
    fullName: string;
    email?: string;
    phoneNumber?: string;
  };
}

class LoanService {
  private baseUrl = '/api/loans';

  async checkoutBooks(userId: string, bookIds: string[], dueDates?: { bookId: string; dueDate: string }[]): Promise<CheckoutBooksResponse> {
    const requestBody: CheckoutBooksRequest = {
      userId,
      bookIds
    };
    
    if (dueDates && dueDates.length > 0) {
      requestBody.dueDates = dueDates;
    }
    
    const response = await apiService.post(`${this.baseUrl}/checkout`, requestBody);
    return response as CheckoutBooksResponse;
  }

  async returnBooks(loanIds: string[]): Promise<ReturnBooksResponse> {
    const response = await apiService.post(`${this.baseUrl}/return`, {
      loanIds
    });
    return response as ReturnBooksResponse;
  }

  async getCurrentLoans(userId: string): Promise<BookLoan[]> {
    const response = await apiService.get<{ loans: BookLoan[] }>(`${this.baseUrl}/current?userId=${userId}`);
    return response.loans || [];
  }

  async getAllLoans(params?: {
    search?: string;
    borrowerName?: string;
    borrowerPhone?: string;
    borrowerEmail?: string;
    bookTitle?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ loans: BookLoan[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.borrowerName) searchParams.append('borrowerName', params.borrowerName);
    if (params?.borrowerPhone) searchParams.append('borrowerPhone', params.borrowerPhone);
    if (params?.borrowerEmail) searchParams.append('borrowerEmail', params.borrowerEmail);
    if (params?.bookTitle) searchParams.append('bookTitle', params.bookTitle);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/all?${queryString}` : `${this.baseUrl}/all`;
    
    const response = await apiService.get<{ loans: BookLoan[]; totalCount?: number; page?: number; pageSize?: number; totalPages?: number }>(endpoint);
    
    // Handle both old format (loans array) and new format (object with pagination)
    if (Array.isArray(response)) {
      return {
        loans: response,
        totalCount: response.length,
        page: 1,
        pageSize: response.length,
        totalPages: 1
      };
    }
    
    return {
      loans: response.loans || [],
      totalCount: response.totalCount || (response.loans?.length || 0),
      page: response.page || 1,
      pageSize: response.pageSize || (response.loans?.length || 0),
      totalPages: response.totalPages || 1
    };
  }

  async getLoanHistory(params?: {
    search?: string;
    borrowerName?: string;
    borrowerPhone?: string;
    borrowerEmail?: string;
    bookTitle?: string;
    status?: 'returned' | 'not_returned' | 'on_time' | 'overdue';
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ loans: BookLoan[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append('search', params.search);
    if (params?.borrowerName) searchParams.append('borrowerName', params.borrowerName);
    if (params?.borrowerPhone) searchParams.append('borrowerPhone', params.borrowerPhone);
    if (params?.borrowerEmail) searchParams.append('borrowerEmail', params.borrowerEmail);
    if (params?.bookTitle) searchParams.append('bookTitle', params.bookTitle);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.startDate) searchParams.append('startDate', params.startDate);
    if (params?.endDate) searchParams.append('endDate', params.endDate);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/history?${queryString}` : `${this.baseUrl}/history`;
    
    const response = await apiService.get<{ loans: BookLoan[]; totalCount?: number; page?: number; pageSize?: number; totalPages?: number }>(endpoint);
    
    // Handle both old format (loans array) and new format (object with pagination)
    if (Array.isArray(response)) {
      return {
        loans: response,
        totalCount: response.length,
        page: 1,
        pageSize: response.length,
        totalPages: 1
      };
    }
    
    return {
      loans: response.loans || [],
      totalCount: response.totalCount || (response.loans?.length || 0),
      page: response.page || 1,
      pageSize: response.pageSize || (response.loans?.length || 0),
      totalPages: response.totalPages || 1
    };
  }

  async getLoanRequests(params?: {
    status?: 'pending' | 'approved' | 'rejected';
    userId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ requests: LoanRequest[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.userId) searchParams.append('userId', params.userId);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/requests?${queryString}` : `${this.baseUrl}/requests`;
    
    const response = await apiService.get<GetLoanRequestsResponse>(endpoint);
    
    return {
      requests: response.requests || [],
      totalCount: response.totalCount || (response.requests?.length || 0),
      page: response.page || 1,
      pageSize: response.pageSize || (response.requests?.length || 0),
      totalPages: response.totalPages || 1
    };
  }

  async approveLoanRequest(requestId: string): Promise<ApproveLoanRequestResponse> {
    const response = await apiService.post<ApproveLoanRequestResponse>(
      `${this.baseUrl}/requests/${requestId}/approve`,
      {}
    );
    return response;
  }

  async rejectLoanRequest(requestId: string, rejectionReason?: string): Promise<RejectLoanRequestResponse> {
    const response = await apiService.post<RejectLoanRequestResponse>(
      `${this.baseUrl}/requests/${requestId}/reject`,
      { rejectionReason }
    );
    return response;
  }

  async exportCurrentLoans(dateFrom?: Date | null, dateTo?: Date | null): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (dateFrom) {
      searchParams.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      searchParams.append('dateTo', dateTo.toISOString().split('T')[0]);
    }
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/export/current?${queryString}` : `${this.baseUrl}/export/current`;
    
    const response = await apiService.get(endpoint);
    return Array.isArray(response) ? response : [];
  }

  async exportLoanRequests(dateFrom?: Date | null, dateTo?: Date | null, status?: string): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (dateFrom) {
      searchParams.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      searchParams.append('dateTo', dateTo.toISOString().split('T')[0]);
    }
    if (status) {
      searchParams.append('status', status);
    }
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/export/requests?${queryString}` : `${this.baseUrl}/export/requests`;
    
    const response = await apiService.get(endpoint);
    return Array.isArray(response) ? response : [];
  }

  async exportLoanHistory(dateFrom?: Date | null, dateTo?: Date | null, status?: string): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (dateFrom) {
      searchParams.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      searchParams.append('dateTo', dateTo.toISOString().split('T')[0]);
    }
    if (status) {
      searchParams.append('status', status);
    }
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/export/history?${queryString}` : `${this.baseUrl}/export/history`;
    
    const response = await apiService.get(endpoint);
    return Array.isArray(response) ? response : [];
  }

  async exportOverdueBooks(dateFrom?: Date | null, dateTo?: Date | null): Promise<any[]> {
    const searchParams = new URLSearchParams();
    if (dateFrom) {
      searchParams.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      searchParams.append('dateTo', dateTo.toISOString().split('T')[0]);
    }
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `${this.baseUrl}/export/overdue?${queryString}` : `${this.baseUrl}/export/overdue`;
    
    const response = await apiService.get(endpoint);
    return Array.isArray(response) ? response : [];
  }
}

export const loanService = new LoanService();
